import type { ReactNode } from 'react';
import { CenteredSpinner } from '~/commonComponents';
import { Navigate, useLocation } from 'react-router';
import ForgotPassword from '~/components/authentication/ForgotPassword';
import UserAuth from '~/components/authentication/UserAuth';
import { CloudflareChallenge } from '~/components/CloudflareChallenge';
import { AdditionalOfficeInformation } from '~/components/registration/AdditionalOfficeInformation';
import { ContactInformation } from '~/components/registration/ContactInformation';
import { GeneralOfficeInformation } from '~/components/registration/GeneralOfficeInformation';
import { WorkerClassification as WorkerClassificationSignUp } from '~/components/registration/WorkerClassification';
import { RegistrationRedirect } from '~/components/AuthRedesign/RegistrationRedirect';
import { ForgotPasswordPage } from '~/components/AuthRedesign/forgotPassword';
import { LoginPage } from '~/components/AuthRedesign/login';
import { AuthShell } from '~/components/AuthRedesign/shared';
import {
  OfficeDetailsPage,
  OfficeSetupPage,
  SignUpLayout,
  SignUpPage,
  YourInfoPage,
} from '~/components/AuthRedesign/signUp';
import { WorkerTypePage } from '~/components/AuthRedesign/signUp/workerType';
import { ReferralLinkRedirect } from '~/components/ReferralLinkRedirect';
import { SignUpRedesign } from '~/constants/ff.posthog';
import { extractReferralCodeFromUrl } from '~/domain/Referral';
import { useFeatureFlagVariantState } from '~/hooks/useFeatureFlagVariantState';
import { routes } from '..';
import { useAppRoutes } from '../hooks';

export const UnauthenticatedRoutes = () => {
  const signUpRedesignStatus = useFeatureFlagVariantState(SignUpRedesign, 'test');
  const { search } = useLocation();

  const shouldUseSignUpRedesign = signUpRedesignStatus === 'enabled';
  const fallbackLoginPath = shouldUseSignUpRedesign ? routes.signUpRedesign.login : routes.login;
  // The legacy auth page hosts both login and sign-up on one screen.
  const signUpPath = shouldUseSignUpRedesign ? routes.signUpRedesign.signUp : routes.login;

  // GOTU-7432: a v2 referral link must be read by a route element before any
  // `<Navigate>` strips it. Registered ahead of `*` on both sides of the
  // sign-up redesign flag; the element itself gates on the referral flag.
  const referralLinkElement = (
    <ReferralLinkRedirect signUpPath={signUpPath} fallbackPath={fallbackLoginPath} />
  );
  const referralLinkRoutes = [
    { path: routes.referralLinkV2, title: 'Referral Link', element: referralLinkElement },
    { path: routes.referralLinkBranch, title: 'Referral Link', element: referralLinkElement },
  ];

  /**
   * GOTU-8354: Branch's desktop fallback does not preserve the deep-link path.
   * It forwards to the site root with the link's params appended —
   * `/?referralCode=<CODE>&branch_url=…` — which matches neither referral route,
   * so `/` fell through to its own element and the code was lost: the redesign
   * root `<Navigate>`s with a bare path, and the legacy root is the auth page,
   * which never reads the query.
   *
   * A route cannot be matched on a query string, so the root's *element* is
   * chosen instead. Only when a code is actually present — an ordinary cold
   * login must never be put behind the referral flag's spinner. `search` alone
   * is read, not the whole URL: the path shape is already routed, and the
   * `branch_url` param carries a percent-encoded copy of the alias path that
   * must not be mistaken for the address bar.
   */
  const hasReferralCodeInQuery = Boolean(extractReferralCodeFromUrl(search));
  const rootElement = (element: ReactNode) =>
    hasReferralCodeInQuery ? referralLinkElement : element;

  const legacyRoutes = [
    { path: routes.login, title: 'Login', element: rootElement(<UserAuth />) },
    { path: routes.forgotPassword, title: 'Forgot Password', element: <ForgotPassword /> },
    {
      title: 'Registration - Contact Information',
      path: routes.contactInformation,
      element: <ContactInformation />,
    },
    {
      title: 'Registration - General Office Information',
      path: routes.generalOfficeInformation,
      element: <GeneralOfficeInformation />,
    },
    {
      title: 'Registration - Additional Office Information',
      path: routes.additionalOfficeInformation,
      element: <AdditionalOfficeInformation isRegistration />,
    },
    {
      title: 'Registration - Worker Classification',
      path: routes.workerClassification,
      element: <WorkerClassificationSignUp />,
    },
  ];

  const redesignRoutes = [
    {
      title: 'Auth Redesign Shell',
      element: <AuthShell />,
      children: [
        { path: routes.signUpRedesign.login, title: 'Login', element: <LoginPage /> },
        { path: routes.signUpRedesign.signUp, title: 'Sign Up', element: <SignUpPage /> },
        {
          title: 'Sign Up Steps',
          element: <SignUpLayout />,
          children: [
            {
              path: routes.signUpRedesign.yourInfo,
              title: 'Your Information',
              element: <YourInfoPage />,
            },
            {
              path: routes.signUpRedesign.officeDetails,
              title: 'Office Details',
              element: <OfficeDetailsPage />,
            },
            {
              path: routes.signUpRedesign.officeSetup,
              title: 'Office Setup',
              element: <OfficeSetupPage />,
            },
            {
              path: routes.signUpRedesign.workerType,
              title: 'Worker Type',
              element: <WorkerTypePage />,
            },
          ],
        },
      ],
    },
    {
      title: 'Auth Redesign Shell (Forgot Password)',
      element: <AuthShell variant="forgot-password" />,
      children: [
        {
          path: routes.signUpRedesign.forgotPassword,
          title: 'Forgot Password',
          element: <ForgotPasswordPage />,
        },
      ],
    },
    { path: '/registration/*', element: <RegistrationRedirect /> },
    {
      path: '/',
      element: rootElement(<Navigate to={routes.signUpRedesign.login} replace />),
    },
  ];

  const sharedRoutes = [
    { path: routes.challenge, title: 'Security Verification', element: <CloudflareChallenge /> },
  ];

  // Viewport-sized wrapper so the centered Spinner fills and centers within it.
  const loadingElement = (
    <div style={{ height: '100vh', display: 'flex' }}>
      <CenteredSpinner />
    </div>
  );

  const resolvedRoutes =
    signUpRedesignStatus === 'loading'
      ? [{ path: '*', element: loadingElement }]
      : [
          ...(shouldUseSignUpRedesign ? redesignRoutes : legacyRoutes),
          ...referralLinkRoutes,
          { path: '*', element: <Navigate to={fallbackLoginPath} replace /> },
        ];

  return useAppRoutes([...sharedRoutes, ...resolvedRoutes]);
};
